import { X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { FocusTrap } from 'focus-trap-react';
import { cn } from './cn';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  hideClose?: boolean;
}

const SIZE = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  hideClose,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const mouseDownOnBackdrop = useRef(false);

  if (!open) return null;

  return createPortal(
    <FocusTrap
      active={open}
      focusTrapOptions={{
        escapeDeactivates: false,
        allowOutsideClick: true,
        fallbackFocus: '[data-modal-panel]',
      }}
    >
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop animate-fade-in"
      onMouseDown={(e) => { mouseDownOnBackdrop.current = e.target === e.currentTarget; }}
      onMouseUp={(e) => { if (mouseDownOnBackdrop.current && e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div
        data-modal-panel
        tabIndex={-1}
        className={cn(
          'w-full bg-surface-raised rounded-lg shadow-modal border border-line overflow-hidden animate-fade-in flex flex-col max-h-[calc(100vh-2rem)] max-h-[calc(100dvh-2rem)] outline-none',
          SIZE[size],
        )}
      >
        {(title || description) && (
          <div className="shrink-0 flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-line">
            <div className="space-y-1 min-w-0">
              {title && (
                <h2 className="text-base font-semibold text-ink leading-tight">
                  {title}
                </h2>
              )}
              {description && (
                <p className="text-sm text-ink-muted leading-relaxed">{description}</p>
              )}
            </div>
            {!hideClose && (
              <button
                onClick={onClose}
                aria-label="Fechar"
                type="button"
                className="shrink-0 -mt-1 -mr-1 p-1.5 rounded-lg text-ink-subtle hover:text-ink hover:bg-surface-muted transition-colors"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        )}
        <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0">{children}</div>
        {footer && (
          <div className="shrink-0 px-6 py-4 border-t border-line bg-surface-muted/60 flex items-center justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
    </FocusTrap>,
    document.body,
  );
}
